<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: CrudIntegration
  Reason: "Übersicht" navigation point — 15 case-type-scoped config tabs. HARD rule: every tab
    panel body is wrapped in @if on the active tab so inactive tabs are not rendered (CONFIG-022).
-->
<div class="cw-overview p-2">
    <p-tabs [value]="activeTab" (valueChange)="onTabChange($event)" [scrollable]="true">
        <p-tablist>
            <p-tab value="basic">Basisdaten</p-tab>
            <p-tab value="businessTypes">Geschäftsarten</p-tab>
            <p-tab value="fields">Felder</p-tab>
            <p-tab value="formerContracts">Vorherige Verträge</p-tab>
            <p-tab value="closureChannels">Abschlusskanäle</p-tab>
            <p-tab value="generalAgencyNumbers">Agenturnummern</p-tab>
            <p-tab value="persons">Personen</p-tab>
            <p-tab value="objects">Objekte</p-tab>
            <p-tab value="coverages">Bausteine/Elementarprodukte</p-tab>
            <p-tab value="documentGenerate">Dokumenterzeugung</p-tab>
            <p-tab value="documentTypes">Dokumenttypen</p-tab>
            <p-tab value="notifications">Benachrichtigungen</p-tab>
            <p-tab value="durations">Vertragslaufzeiten</p-tab>
            <p-tab value="payment">Zahlung</p-tab>
            <p-tab value="questions">Fragen</p-tab>
        </p-tablist>

        <p-tabpanels>

            <!-- 1) Basisdaten -->
            <p-tabpanel value="basic">
                @if (activeTab === 'basic') {
                    <div class="flex justify-content-end mb-2">
                        <p-button
                            [label]="editBasic ? 'Ansicht' : 'Bearbeiten'"
                            [icon]="editBasic ? 'fa-regular fa-eye' : 'fa-regular fa-pen'"
                            severity="secondary"
                            [text]="true"
                            (onClick)="toggleBasicEdit()">
                        </p-button>
                    </div>
                    @if (!editBasic && basicDataWidget) {
                        <mvs-widget [widgetData]="basicDataWidget"></mvs-widget>
                    }
                    @if (editBasic && basicEditWidget) {
                        <mvs-widget [widgetData]="basicEditWidget"></mvs-widget>
                    }

                    <!-- [CW-PERSON-REFACTOR][WP-D3] The eight routing fields of the field group
                         „Personen-Konfiguration“ are edited by the generic form above. An INHERITED
                         value is invisible there: the field is genuinely empty, the value lives in
                         the linked Routing-Profil. This panel names, per axis, which of the three
                         stages (Vorgangstyp -> Routing-Profil -> nothing) actually answers.
                         Deliberately BELOW the form and read-only — typing into the form field would
                         turn the inheritance into a local copy. -->
                    <div class="mt-4">
                        <cw-case-type-routing-inheritance [caseTypeId]="caseTypeId">
                        </cw-case-type-routing-inheritance>
                    </div>
                }
            </p-tabpanel>

            <!-- 2) Geschäftsarten -->
            <p-tabpanel value="businessTypes">
                @if (activeTab === 'businessTypes' && businessTypeWidget) {
                    <mvs-widget [widgetData]="businessTypeWidget"></mvs-widget>
                }
            </p-tabpanel>

            <!-- 3) Felder -->
            <p-tabpanel value="fields">
                @if (activeTab === 'fields' && fieldWidget) {
                    <mvs-widget [widgetData]="fieldWidget"></mvs-widget>
                }
            </p-tabpanel>

            <!-- 4) Vorherige Verträge -->
            <p-tabpanel value="formerContracts">
                @if (activeTab === 'formerContracts') {
                    <div class="grid">
                        <div class="col-12 lg:col-6">
                            @if (formerContractTypeWidget) {
                                <mvs-widget [widgetData]="formerContractTypeWidget"></mvs-widget>
                            }
                        </div>
                        <div class="col-12 lg:col-6">
                            @if (formerProductWidget) {
                                <mvs-widget [widgetData]="formerProductWidget"></mvs-widget>
                            }
                        </div>
                    </div>
                }
            </p-tabpanel>

            <!-- 5) Abschlusskanäle (parent → Kanalfelder) -->
            <p-tabpanel value="closureChannels">
                @if (activeTab === 'closureChannels') {
                    <div class="grid">
                        <div class="col-12 lg:col-5">
                            @if (closureChannelWidget) {
                                <mvs-widget
                                    [widgetData]="closureChannelWidget"
                                    (onObjectSelect)="handleClosureChannelSelect($event)">
                                </mvs-widget>
                            }
                        </div>
                        <div class="col-12 lg:col-7">
                            @if (closureChannelFieldWidget) {
                                <mvs-widget [widgetData]="closureChannelFieldWidget"></mvs-widget>
                            } @else {
                                <div class="cw-hint p-4 text-center text-500">Bitte einen Abschlusskanal wählen.</div>
                            }
                        </div>
                    </div>
                }
            </p-tabpanel>

            <!-- 6) Agenturnummern. Flaches Kind des Vorgangstyps, steht direkt neben den
                 Abschlusskanälen: der optionale Abschlusskanal einer Zeile schränkt die
                 Vermittlernummer auf genau diesen Kanal ein — bleibt er leer, gilt die Nummer für
                 den gesamten Vorgangstyp. -->
            <p-tabpanel value="generalAgencyNumbers">
                @if (activeTab === 'generalAgencyNumbers' && generalAgencyNumberWidget) {
                    <mvs-widget [widgetData]="generalAgencyNumberWidget"></mvs-widget>
                }
            </p-tabpanel>

            <!-- 7) Personen (parent → Personenfelder) -->
            <p-tabpanel value="persons">
                @if (activeTab === 'persons') {
                    <!-- [CW-PERSON-REFACTOR][E1] The eight address/contact ROUTING attributes were
                         maintained on the ROLE until E1 and are now configured once per Vorgangstyp.
                         Their deprecated copies are still rendered in the role form below (they remain
                         the live source until the E1 step-2 migration) and carry „— veraltet“ in their
                         labels, so without this hint a configurator has no way of telling which of the
                         two he is looking at. What stayed on the role is the BEDARF. -->
                    <p-message severity="secondary" styleClass="w-full mb-3">
                        <div class="flex flex-column gap-1">
                            <span class="font-semibold">Anschrift und Kontakt: Bedarf hier, Zuordnung
                                zentral</span>
                            <span class="text-sm">
                                Hier legen Sie je Rolle den <b>Bedarf</b> fest — ob eine Anschrift, eine
                                Telefon- oder Mobilnummer und eine E-Mail-Adresse erwartet werden. Aus
                                <b>welchem</b> Kontakttyp und welcher Adressart diese Werte gelesen
                                werden, gilt für den gesamten Vorgangstyp und wird unter
                                <b>Basisdaten › Personen-Konfiguration</b> gepflegt. Die gleichnamigen
                                Felder unten sind mit „— veraltet“ gekennzeichnet und entfallen nach der
                                Umstellung der Konfiguration.
                            </span>
                        </div>
                    </p-message>
                    <div class="grid">
                        <div class="col-12 lg:col-5">
                            @if (personWidget) {
                                <mvs-widget
                                    [widgetData]="personWidget"
                                    (onObjectSelect)="handlePersonSelect($event)">
                                </mvs-widget>
                            }
                        </div>
                        <div class="col-12 lg:col-7">
                            @if (personFieldWidget) {
                                <mvs-widget class="block mb-3" [widgetData]="personFieldWidget"></mvs-widget>
                                <!-- Second sibling table of the role: the source attributes on
                                     pm.Person / pm.PersonDetail and their requirement. NOT part of the
                                     generic field pool above — these values are read straight off the
                                     person instead of being captured in the case. -->
                                @if (personReqFieldWidget) {
                                    <mvs-widget [widgetData]="personReqFieldWidget"></mvs-widget>
                                }
                            } @else {
                                <div class="cw-hint p-4 text-center text-500">Bitte eine Person wählen.</div>
                            }
                        </div>
                    </div>
                }
            </p-tabpanel>

            <!-- 8) Objekte (parent → Objektfelder + versicherbare Objektarten + Definitions-
                 Zuordnungen; Objektfeld → Zielfeld-Zuordnungen) -->
            <p-tabpanel value="objects">
                @if (activeTab === 'objects') {
                    <div class="grid">
                        <div class="col-12 lg:col-5">
                            @if (objectWidget) {
                                <mvs-widget
                                    [widgetData]="objectWidget"
                                    (onObjectSelect)="handleObjectSelect($event)">
                                </mvs-widget>
                            }
                        </div>
                        <div class="col-12 lg:col-7">
                            @if (objectFieldWidget) {
                                <!-- The object-field table doubles as the master of the IoT mappings:
                                     selecting a row opens, below it, which insurable-object-type field
                                     is mirrored into that field, per object type. -->
                                <mvs-widget
                                    class="block mb-3"
                                    [widgetData]="objectFieldWidget"
                                    (onObjectSelect)="handleObjectFieldSelect($event)">
                                </mvs-widget>
                                @if (objectFieldIotFieldWidget) {
                                    <mvs-widget class="block mb-3" [widgetData]="objectFieldIotFieldWidget"></mvs-widget>
                                } @else {
                                    <div class="cw-hint p-3 mb-3 text-center text-500">
                                        Bitte ein Objektfeld wählen, um die Zielfeld-Zuordnungen zu sehen.
                                    </div>
                                }
                                @if (objectInsurableObjectTypeWidget) {
                                    <mvs-widget class="block mb-3" [widgetData]="objectInsurableObjectTypeWidget"></mvs-widget>
                                }
                                <!-- The second row kind of the same entity. It is owned by the object
                                     rather than by an object field, so it sits outside the object-field
                                     drilldown and shows even when no object field is selected. -->
                                @if (objectDefinitionIotFieldWidget) {
                                    <p-message severity="secondary" styleClass="w-full mb-2">
                                        <span class="text-sm">
                                            Eine <b>Definitions-Zuordnung</b> belegt <b>kein Feld des
                                            Vorgangstyps</b>. Sie erklärt ein Objekttyp-Feld an der
                                            Objektdefinition als benötigt und stellt seinen Wert unter einem
                                            eigenen Alias als <code>io_&lt;Alias&gt;</code> im Austauschmodell
                                            bereit. Zuordnungen auf ein Vorgangsfeld pflegen Sie oben als
                                            <b>Zielfeld-Zuordnung</b> am jeweiligen Objektfeld.
                                        </span>
                                    </p-message>
                                    <mvs-widget [widgetData]="objectDefinitionIotFieldWidget"></mvs-widget>
                                }
                            } @else {
                                <div class="cw-hint p-4 text-center text-500">Bitte ein Objekt wählen.</div>
                            }
                        </div>
                    </div>
                }
            </p-tabpanel>

            <!-- 9) Bausteine/Elementarprodukte (parent → Felder + Objekte + Personen) -->
            <p-tabpanel value="coverages">
                @if (activeTab === 'coverages') {
                    <div class="grid">
                        <div class="col-12 lg:col-5">
                            @if (coverageWidget) {
                                <mvs-widget
                                    [widgetData]="coverageWidget"
                                    (onObjectSelect)="handleCoverageSelect($event)">
                                </mvs-widget>
                            }
                        </div>
                        <div class="col-12 lg:col-7">
                            @if (coverageFieldWidget) {
                                <mvs-widget class="block mb-3" [widgetData]="coverageFieldWidget"></mvs-widget>
                                @if (coverageObjectWidget) {
                                    <mvs-widget class="block mb-3" [widgetData]="coverageObjectWidget"></mvs-widget>
                                }
                                @if (coveragePersonWidget) {
                                    <mvs-widget class="block mb-3" [widgetData]="coveragePersonWidget"></mvs-widget>
                                }
                                <!--
                                  [CW-VP-DIM] Betragsregeln des gewählten Bausteins: Min/Max/Schrittweite
                                  je Wertschlüssel — die Zeilen, gegen die das Backend validiert und aus
                                  denen die Erfassung ihre Hinweise rendert.
                                -->
                                @if (coverageAmountRuleWidget) {
                                    <mvs-widget [widgetData]="coverageAmountRuleWidget"></mvs-widget>
                                }
                            } @else {
                                <div class="cw-hint p-4 text-center text-500">Bitte einen Baustein wählen.</div>
                            }
                        </div>
                    </div>
                }
            </p-tabpanel>

            <!-- 10) Dokumenterzeugung -->
            <p-tabpanel value="documentGenerate">
                @if (activeTab === 'documentGenerate' && documentGenerateWidget) {
                    <mvs-widget [widgetData]="documentGenerateWidget"></mvs-widget>
                }
            </p-tabpanel>

            <!-- 11) Dokumenttypen -->
            <p-tabpanel value="documentTypes">
                @if (activeTab === 'documentTypes' && documentTypeWidget) {
                    <mvs-widget [widgetData]="documentTypeWidget"></mvs-widget>
                }
            </p-tabpanel>

            <!-- 12) Benachrichtigungen -->
            <p-tabpanel value="notifications">
                @if (activeTab === 'notifications') {
                    <div class="grid">
                        <div class="col-12 lg:col-6">
                            @if (notificationTypeWidget) {
                                <mvs-widget [widgetData]="notificationTypeWidget"></mvs-widget>
                            }
                        </div>
                        <div class="col-12 lg:col-6">
                            @if (notificationGenerateWidget) {
                                <mvs-widget [widgetData]="notificationGenerateWidget"></mvs-widget>
                            }
                        </div>
                    </div>
                }
            </p-tabpanel>

            <!-- 13) Vertragslaufzeiten. Steht neben der Zahlung, weil beide die WÄHLBAREN KONDITIONEN
                 des neuen Vertrags halten (wie lange er läuft, wie er gezahlt wird) — und bewusst nicht
                 unter „Vorherige Verträge“, wo es um den abgelösten Vertrag geht. Die Laufzeiten je
                 Produktlinie hängen an caseTypeProduct und stehen deshalb unter Produkte. -->
            <p-tabpanel value="durations">
                @if (activeTab === 'durations') {
                    <p-message
                        severity="secondary"
                        text="Diese Laufzeiten bilden die Auswahl für das Vertragsende im Antrag; ohne Zeile bleibt es beim freien Datumsfeld. Bei „Feste Laufzeit“ ist „Laufzeit (Jahre)“ Pflicht (mindestens 1 Jahr), bei „Unbefristet“ muss das Feld leer bleiben — und „Unbefristet“ ist je Vorgangstyp nur einmal zulässig. Konfiguriert ein Produkt eigene Vertragslaufzeiten, gilt für diese Produktlinie ausschließlich dessen Liste."
                        styleClass="w-full mb-3">
                    </p-message>
                    @if (durationWidget) {
                        <mvs-widget [widgetData]="durationWidget"></mvs-widget>
                    }
                }
            </p-tabpanel>

            <!-- 14) Zahlung -->
            <p-tabpanel value="payment">
                @if (activeTab === 'payment') {
                    <div class="grid">
                        <div class="col-12 lg:col-6">
                            @if (paymentFrequencyWidget) {
                                <mvs-widget [widgetData]="paymentFrequencyWidget"></mvs-widget>
                            }
                        </div>
                        <div class="col-12 lg:col-6">
                            @if (paymentMethodWidget) {
                                <mvs-widget [widgetData]="paymentMethodWidget"></mvs-widget>
                            }
                        </div>
                    </div>
                }
            </p-tabpanel>

            <!-- 15) Fragen (Fragensektion → Fragen). CASE-WIDE sections only; the product-side
                 sections live under Produkte > Fragen, because their FK is caseTypeProduct. -->
            <p-tabpanel value="questions">
                @if (activeTab === 'questions') {
                    <div class="grid">
                        <div class="col-12 lg:col-5">
                            @if (questionSectionWidget) {
                                <mvs-widget
                                    [widgetData]="questionSectionWidget"
                                    (onObjectSelect)="handleQuestionSectionSelect($event)">
                                </mvs-widget>
                            }
                        </div>
                        <div class="col-12 lg:col-7">
                            @if (questionWidget) {
                                <mvs-widget [widgetData]="questionWidget"></mvs-widget>
                            } @else {
                                <div class="cw-hint p-4 text-center text-500">Bitte eine Fragensektion wählen.</div>
                            }
                        </div>
                    </div>
                }
            </p-tabpanel>

        </p-tabpanels>
    </p-tabs>
</div>
